import { useEffect, useRef } from 'react';

import { ApiError } from '../../../lib/api';
import { referralsApi } from '../api/referrals.api';

const PENDING_REFERRAL_STORAGE_KEY = 'ninpo.pendingReferralCode';

type Toast = (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;

const readPending = (): string => {
  try {
    return (window.localStorage.getItem(PENDING_REFERRAL_STORAGE_KEY) || '').trim().toUpperCase();
  } catch {
    return '';
  }
};

const clearPending = () => {
  try {
    window.localStorage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode); nothing was persisted.
  }
};

/** Remember a friend's code from a `/signup?ref=` link until the visitor has a signed-in session. */
export const rememberPendingReferralCode = (code: string) => {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return;
  try {
    window.localStorage.setItem(PENDING_REFERRAL_STORAGE_KEY, normalized);
  } catch {
    // Storage unavailable; the customer can still add the code under Account > Invite Friends.
  }
};

/**
 * Claims the remembered referral code once per signed-in session. Email signups that need
 * verification are not signed in at signup time, so the claim happens on their first sign-in.
 * Accepted or rejected (4xx) clears the code; network and server errors keep it for next time.
 */
export function usePendingReferralClaim(userId: string | null | undefined, addToast: Toast) {
  const attemptedForUser = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || attemptedForUser.current === userId) return;
    const code = readPending();
    if (!code) return;
    attemptedForUser.current = userId;
    referralsApi
      .claim(code)
      .then((summary) => {
        clearPending();
        addToast(
          summary.rewardLabel
            ? `Friend code ${code} added. You both get ${summary.rewardLabel} after your first delivered order.`
            : `Friend code ${code} added.`,
          'success'
        );
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          clearPending();
          addToast(`Friend code ${code} was not applied: ${error.message}`, 'warning');
        }
      });
  }, [userId, addToast]);
}
